'use client';

import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { DEFAULT_LOCALE } from '@/constants/locales';
import { getEntryWithFields } from '@/lib/contentstack/entries';
import { isLanguageSupported } from '@/lib/contentstack/language';
import { localizeHref } from '@/utils/localize-href';
import { IStandardContentPage } from '@/.generated';

// Resolved link details for a page reference: the localized URL and the
// referenced entry's title (used as a label fallback when no explicit title exists).
export interface PageReferenceLink {
  href: string | undefined;
  title: string | undefined;
}

// Page titles carry a brand suffix (e.g. "Grid management | SRP") — keep only the
// chunk before "|" so the nav label stays concise.
export const cleanPageTitle = (pageTitle?: string): string | undefined =>
  pageTitle?.split('|')[0].trim() || undefined;

// Resolve both the URL and title for a page reference. Page-reference links in the
// CMS often omit a manual link title, so the referenced page's title is the label.
export const usePageReferenceLink = (
  pageReference: IStandardContentPage[] | undefined,
  fallbackHref: string | undefined,
  fallbackTitle?: string | undefined
): PageReferenceLink => {
  const pageRef = pageReference?.[0];
  const params = useParams();
  const localeParam = params?.locale as string | undefined;
  const language = localeParam && isLanguageSupported(localeParam) ? localeParam : DEFAULT_LOCALE;

  // Server-included references (via includeReference/include_all) already carry
  // url/title/locale — resolve them immediately so links render without a client
  // fetch or flicker. A fallback-locale entry (locale ≠ active language) keeps its
  // unprefixed default-locale URL: the page only exists in English.
  const isIncluded = typeof pageRef?.url === 'string' && !!pageRef.url;
  const includedHref = isIncluded
    ? pageRef.locale === language || !pageRef.locale
      ? localizeHref(pageRef.url, language)
      : pageRef.url
    : undefined;
  const includedTitle = isIncluded ? fallbackTitle || cleanPageTitle(pageRef.title) : undefined;

  // fallbackHref is a raw CMS string with no locale context — leave it raw and
  // let the caller decide whether to localize (e.g. english-only links must not
  // be prefixed). We only localize URLs we ourselves fetched in `language`.
  const [href, setHref] = useState<string | undefined>(includedHref ?? fallbackHref);
  const [title, setTitle] = useState<string | undefined>(includedTitle ?? fallbackTitle);

  useEffect(() => {
    // Reference resolved server-side — sync state and skip the client CMS fetch.
    if (includedHref) {
      setHref(includedHref);
      setTitle(includedTitle);
      return;
    }

    const uid = pageRef?.uid;
    const contentTypeUid = pageRef?._content_type_uid;
    if (!uid || !contentTypeUid) {
      setHref(fallbackHref);
      setTitle(fallbackTitle);
      return;
    }

    // Fetch URL + title in active locale; fall back to default locale if not localized
    const fetchUrl = async () => {
      const entry = await getEntryWithFields<{ url?: string; title?: string }>(
        uid,
        contentTypeUid,
        ['url', 'title'],
        language
      );

      if (entry?.url) {
        setHref(localizeHref(entry.url, language));
        setTitle(fallbackTitle || cleanPageTitle(entry.title));
        return;
      }

      if (language !== DEFAULT_LOCALE) {
        // Entry not localized → use default-locale URL without the active-locale
        // prefix (the page only exists in English).
        const defaultEntry = await getEntryWithFields<{ url?: string; title?: string }>(
          uid,
          contentTypeUid,
          ['url', 'title'],
          DEFAULT_LOCALE
        );
        setHref(defaultEntry?.url ?? fallbackHref);
        setTitle(fallbackTitle || cleanPageTitle(defaultEntry?.title));
        return;
      }

      setHref(fallbackHref);
      setTitle(fallbackTitle);
    };

    fetchUrl();
  }, [
    pageRef?.uid,
    pageRef?._content_type_uid,
    fallbackHref,
    fallbackTitle,
    language,
    includedHref,
    includedTitle,
  ]);

  return { href, title };
};

// Backwards-compatible helper for callers that only need the resolved URL.
export const usePageReferenceUrl = (
  pageReference: IStandardContentPage[] | undefined,
  fallbackHref: string | undefined
): string | undefined => usePageReferenceLink(pageReference, fallbackHref).href;
